/**
 * AI Shop Assistant — inline hero variant (claude.ai-style).
 *
 * Renders the chat as a primary page section instead of a floating panel.
 * Shares sessionStorage state with the floating widget so navigation
 * between the homepage and product pages carries the conversation.
 */
(function () {
  'use strict';

  const root = document.querySelector('[data-ai-chat-hero]');
  if (!root) return;

  const endpoint = root.getAttribute('data-endpoint');
  if (!endpoint) {
    console.warn('[ai-chat-hero] missing data-endpoint');
    return;
  }

  const i18n = {
    locale:       root.getAttribute('data-locale') || 'en',
    greeting:     root.getAttribute('data-greeting') || '',
    errRateLimit: root.getAttribute('data-error-rate-limit') || 'Too many requests — please wait a moment.',
    errNetwork:   root.getAttribute('data-error-network') || 'Connection failed. Please try again later.',
    errGeneric:   root.getAttribute('data-error-generic') || 'Something went wrong. Please try again later.',
    errEmpty:     root.getAttribute('data-error-empty') || 'Sorry, I have no answer right now.',
    thinkingPhrases: (root.getAttribute('data-thinking-phrases') || 'Searching…|One moment…|Almost there…').split('|').filter(Boolean),
  };

  const body = root.querySelector('[data-ai-chat-hero-body]');
  const form = root.querySelector('[data-ai-chat-hero-form]');
  const input = root.querySelector('[data-ai-chat-hero-input]');
  const submit = root.querySelector('[data-ai-chat-hero-submit]');
  const suggestions = root.querySelectorAll('[data-ai-chat-hero-suggestion]');

  // Shared sessionStorage key — same one the floating widget uses, so state
  // carries across both UIs.
  const STORAGE_KEY = 'evscooter:ai-chat:v1';
  const MAX_PERSISTED_MESSAGES = 30;

  /** @type {{role:string,content:string,products?:object[]}[]} */
  let messages = [];

  function loadState() {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed.messages)) {
        messages = parsed.messages.slice(-MAX_PERSISTED_MESSAGES);
      }
    } catch {}
  }

  function saveState() {
    try {
      // Preserve the floating-widget's `isOpen` flag if present.
      let isOpen = false;
      try {
        const prev = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}');
        isOpen = !!prev.isOpen;
      } catch {}
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
        messages: messages.slice(-MAX_PERSISTED_MESSAGES),
        isOpen,
      }));
    } catch {}
  }

  async function readCartContext() {
    try {
      const res = await fetch('/cart.js', {
        headers: { 'Accept': 'application/json' },
        credentials: 'same-origin',
      });
      if (!res.ok) return null;
      const cart = await res.json();
      return {
        itemCount: Number(cart.item_count || 0),
        totalPrice: Number(cart.total_price || 0),
        currency: cart.currency || 'EUR',
        items: (cart.items || []).slice(0, 10).map((item) => ({
          title: item.product_title || item.title || '',
          handle: item.handle || '',
          variantTitle: item.variant_title || '',
          variantId: String(item.variant_id || item.id || ''),
          quantity: Number(item.quantity || 0),
        })),
      };
    } catch {
      return null;
    }
  }

  function collectRecentProducts() {
    const out = [];
    const seen = new Set();
    for (let i = messages.length - 1; i >= 0; i--) {
      const products = Array.isArray(messages[i].products) ? messages[i].products : [];
      for (const p of products) {
        const key = p.variantId || p.id || p.handle;
        if (!key || seen.has(key)) continue;
        seen.add(key);
        out.push({
          id: p.id || '',
          variantId: p.variantId || '',
          title: p.title || '',
          handle: p.handle || '',
          url: p.url || '',
          price: p.price || '',
          currency: p.currency || '',
          available: p.available !== false,
        });
        if (out.length >= 12) return out;
      }
    }
    return out;
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    })[c]);
  }

  function formatPrice(amount, currency) {
    if (!amount) return '';
    const n = Number(amount);
    if (Number.isNaN(n)) return amount;
    const localeTag = (i18n.locale || 'en').replace('_', '-');
    try {
      return new Intl.NumberFormat(localeTag, { style: 'currency', currency: currency || 'EUR' }).format(n);
    } catch {
      return `${n.toFixed(2)} ${currency || 'EUR'}`;
    }
  }

  function safeProductUrl(url, handle) {
    const fallback = handle ? `/products/${encodeURIComponent(String(handle).replace(/^\/+/, ''))}` : '#';
    const value = String(url || '').trim();
    if (!value) return fallback;
    if (value.startsWith('/') && !value.startsWith('//')) return value;
    try {
      const parsed = new URL(value);
      if (parsed.protocol === 'http:' || parsed.protocol === 'https:') {
        return parsed.href;
      }
    } catch {
      // Fall through to the known-safe product handle URL.
    }
    return fallback;
  }

  function renderUser(text) {
    const el = document.createElement('div');
    el.className = 'ai-chat-hero__msg ai-chat-hero__msg--user';
    el.textContent = text;
    body.appendChild(el);
  }

  function renderAssistant(text, products) {
    const el = document.createElement('div');
    el.className = 'ai-chat-hero__msg ai-chat-hero__msg--assistant';
    el.textContent = text;
    body.appendChild(el);

    if (Array.isArray(products) && products.length > 0) {
      const grid = document.createElement('div');
      grid.className = 'ai-chat-hero__products';
      products.slice(0, 6).forEach((p) => {
        const card = document.createElement('div');
        card.className = 'ai-chat-hero__product';
        const a = document.createElement('a');
        a.className = 'ai-chat-hero__product-link';
        a.href = safeProductUrl(p.url, p.handle);
        a.rel = 'noopener';
        const img = p.imageUrl
          ? `<img class="ai-chat-hero__product-img" src="${escapeHtml(p.imageUrl)}" alt="${escapeHtml(p.title)}" loading="lazy" />`
          : '<div class="ai-chat-hero__product-img"></div>';
        a.innerHTML = `
          ${img}
          <div class="ai-chat-hero__product-body">
            <p class="ai-chat-hero__product-title">${escapeHtml(p.title)}</p>
            <p class="ai-chat-hero__product-price">${formatPrice(p.price, p.currency)}</p>
          </div>`;
        card.appendChild(a);
        if (p.variantId && p.available) {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'ai-chat-hero__cart';
          btn.textContent = 'In den Warenkorb';
          btn.addEventListener('click', () => addToCart(btn, p.variantId));
          card.appendChild(btn);
        } else if (p.available === false) {
          const oos = document.createElement('p');
          oos.className = 'ai-chat-hero__product-oos';
          oos.textContent = 'Aktuell nicht verfügbar';
          card.appendChild(oos);
        }
        grid.appendChild(card);
      });
      body.appendChild(grid);
    }
  }

  async function addToCart(button, variantId) {
    if (!variantId || button.disabled) return;
    const original = button.textContent;
    button.disabled = true;
    button.textContent = 'Wird hinzugefügt…';
    try {
      const parsed = await performCartAction({ type: 'add_item', variantId, quantity: 1 });
      refreshCartUI(parsed, button);
      button.textContent = 'Hinzugefügt';
      document.dispatchEvent(new CustomEvent('ai-shop-assistant:cart:add', { detail: { variantId, cart: parsed } }));
      setTimeout(() => { button.textContent = original; button.disabled = false; }, 1800);
    } catch (err) {
      button.textContent = 'Fehler';
      setTimeout(() => { button.textContent = original; button.disabled = false; }, 1800);
    }
  }

  async function performCartAction(action) {
    const variantId = action && action.variantId;
    if (!variantId) throw new Error('missing variantId');
    const type = action.type === 'change_item' ? 'change_item' : 'add_item';
    const endpoint = type === 'change_item' ? '/cart/change.js' : '/cart/add.js';
    const payload = {
      id: Number(variantId),
      quantity: Number.isFinite(Number(action.quantity)) ? Number(action.quantity) : 1,
    };
    const cartSections = getCartSections();
    if (cartSections.length > 0) {
      payload.sections = cartSections.map((section) => section.section);
      payload.sections_url = window.location.pathname;
    }
    const res = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error('cart action failed');
    return res.json();
  }

  async function executeCartActions(actions) {
    if (!Array.isArray(actions) || actions.length === 0) return;
    for (const action of actions.slice(0, 3)) {
      try {
        const parsed = await performCartAction(action);
        refreshCartUI(parsed, null);
        document.dispatchEvent(new CustomEvent('ai-shop-assistant:cart:action', { detail: { action, cart: parsed } }));
        appendAssistant(action.type === 'change_item' && Number(action.quantity) === 0
          ? 'Erledigt. Ich habe den Artikel aus dem Warenkorb entfernt.'
          : 'Erledigt. Ich habe den Warenkorb aktualisiert.');
      } catch (err) {
        appendError('Ich konnte den Warenkorb gerade nicht aktualisieren.');
      }
    }
  }

  function getCartSections() {
    try {
      if (window.miniCart && typeof window.miniCart.getSectionsToRender === 'function') {
        return window.miniCart.getSectionsToRender();
      }
    } catch {}
    return [];
  }

  function refreshCartUI(parsed, opener) {
    try {
      if (window.miniCart && parsed && parsed.sections && typeof window.miniCart.renderContents === 'function') {
        window.miniCart.renderContents(parsed);
      }
      if (window.miniCart && typeof window.miniCart.open === 'function') {
        window.miniCart.open(opener);
      }
    } catch {}
    try {
      if (window.PUB_SUB_EVENTS && typeof window.publish === 'function') {
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
          source: 'ai-shop-assistant',
          cartData: parsed,
          variantId: parsed && (parsed.variant_id || parsed.id),
        });
        window.publish(window.PUB_SUB_EVENTS.cartItemAdd, {
          source: 'ai-shop-assistant',
          cartData: parsed,
          variantId: parsed && (parsed.variant_id || parsed.id),
        });
      }
    } catch {}
  }

  function renderError(text) {
    const el = document.createElement('div');
    el.className = 'ai-chat-hero__msg ai-chat-hero__msg--error';
    el.textContent = text;
    body.appendChild(el);
  }

  // Smart scroll — only auto-scroll if user is already at the bottom.
  function isAtBottom() {
    return body.scrollHeight - body.scrollTop - body.clientHeight < 80;
  }
  function scrollToBottomIfNear(force) {
    if (force || isAtBottom()) {
      requestAnimationFrame(() => {
        const last = body.lastElementChild;
        if (last && last.scrollIntoView) last.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });
    }
  }

  function appendUser(text) {
    renderUser(text);
    scrollToBottomIfNear(true);
  }
  function appendAssistant(text, products) {
    const wasNear = isAtBottom();
    renderAssistant(text, products);
    scrollToBottomIfNear(wasNear);
  }
  function appendError(text) {
    renderError(text);
    scrollToBottomIfNear(false);
  }

  let typingInterval = null;
  function setTyping(on) {
    const existing = body.querySelector('.ai-chat-hero__typing');
    if (on && !existing) {
      const wasNear = isAtBottom();
      const t = document.createElement('div');
      t.className = 'ai-chat-hero__typing';
      const phrase = document.createElement('span');
      phrase.className = 'ai-chat-hero__typing-phrase';
      phrase.textContent = i18n.thinkingPhrases[0] || '';
      t.appendChild(phrase);
      body.appendChild(t);
      scrollToBottomIfNear(wasNear);

      let i = 0;
      typingInterval = setInterval(() => {
        i = (i + 1) % i18n.thinkingPhrases.length;
        phrase.classList.add('is-fading');
        setTimeout(() => {
          phrase.textContent = i18n.thinkingPhrases[i];
          phrase.classList.remove('is-fading');
        }, 200);
      }, 2400);
    } else if (!on && existing) {
      if (typingInterval) { clearInterval(typingInterval); typingInterval = null; }
      existing.remove();
    }
  }

  function rehydrate() {
    body.innerHTML = '';
    messages.forEach((m) => {
      if (m.role === 'user') renderUser(m.content);
      else renderAssistant(m.content, m.products);
    });
    scrollIntoView();
  }

  async function send(text) {
    appendUser(text);
    messages.push({ role: 'user', content: text });
    saveState();
    submit.disabled = true;
    input.disabled = true;
    setTyping(true);

    try {
      const cart = await readCartContext();
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: messages
            .filter((m) => m.role === 'user' || m.role === 'assistant')
            .slice(-20)
            .map((m) => ({ role: m.role, content: m.content })),
          cart,
          recentProducts: collectRecentProducts(),
          locale: i18n.locale,
          pageContext: {
            type: root.getAttribute('data-page-type') || 'home',
            url: window.location.pathname,
          },
        }),
      });
      if (res.status === 429) {
        setTyping(false);
        appendError(i18n.errRateLimit);
        return;
      }
      if (!res.ok) {
        setTyping(false);
        appendError(i18n.errGeneric);
        return;
      }
      const data = await res.json();
      setTyping(false);
      const reply = (data && data.reply) || i18n.errEmpty;
      const products = (data && data.products) || undefined;
      appendAssistant(reply, products);
      messages.push({ role: 'assistant', content: reply, products });
      saveState();
      await executeCartActions(data && data.cartActions);
    } catch (e) {
      console.error('[ai-chat-hero] error', e);
      setTyping(false);
      appendError(i18n.errNetwork);
    } finally {
      submit.disabled = false;
      input.disabled = false;
      input.focus();
    }
  }

  form.addEventListener('submit', (e) => {
    e.preventDefault();
    const text = (input.value || '').trim();
    if (!text) return;
    input.value = '';
    send(text);
  });

  suggestions.forEach((btn) => {
    btn.addEventListener('click', () => {
      const text = btn.getAttribute('data-text') || btn.textContent.trim();
      if (!text) return;
      send(text);
    });
  });

  // --- init ---
  loadState();
  if (messages.length > 0) {
    rehydrate();
  } else if (i18n.greeting) {
    renderAssistant(i18n.greeting);
    messages.push({ role: 'assistant', content: i18n.greeting });
    saveState();
  }
  // Don't autofocus on mobile — would force the keyboard open on every visit.
  if (window.matchMedia && !window.matchMedia('(max-width: 600px)').matches) {
    setTimeout(() => input && input.focus(), 100);
  }
})();
